import {
  CometUserRoleType,
  LoadingConstants,
  RentalManagerUserManagementCompanyWebApiHandler_getPendingProfile,
  RentalManagerUserManagementCompanyWebApiHandler_getPendingUserProperties,
  RentalManagerUserManagementCompanyWebApiHandler_getUserProfiles,
  RentalManagerUserManagementCompanyWebApiHandler_updateUserSettings,
  UserPermissionsConstants,
} from '@zg-rentals/rental-manager-env';
import type { RentalManagerUserProfileUpdateRequestDto } from '@zg-rentals/rental-manager-env';
import type { UserManagementInviteUserRequestDto } from '@zg-rentals/ts-user-management-client';
import {
  UmcCompanyOverviewWebApiHandler_createUserInvitation,
  UmcCompanyOverviewWebApiHandler_getUserCompanyProperties,
} from '@zg-rentals/ts-user-management-client';
import DispatchConstants from '../../DispatchConstants';
import type { CompanyPropertyType } from '../../types/CompanyProperty';
import { CompanyProperty } from '../../types/CompanyProperty';
import { CompanyUser } from '../../types/CompanyUser';

const UserManagementActions = {
  clearUserManagementProfiles() {
    return {
      type: DispatchConstants.USER_MANAGEMENT_USER_PFOFILE_INIT,
    };
  },
  getCurrentUserInfo(userBrand: string, userToken: string) {
    // @ts-ignore
    return (dispatch) => {
      dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_LOADING });
      return RentalManagerUserManagementCompanyWebApiHandler_getUserProfiles({ userBrand, userToken })
        .then((result) => {
          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_SUCCESS,
            payload: { user: CompanyUser(result.userProfiles[0]) },
          });
        })
        .catch((error) => {
          const errorMessage = error.message || 'Failed to get user information.';
          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_FAIL,
            payload: { error: errorMessage },
          });
        });
    };
  },
  getCurrentUserProperties(userBrand: string, userToken: string, onlyUnpaidProperties = false) {
    // @ts-ignore
    return (dispatch) => {
      dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_LOADING });
      return UmcCompanyOverviewWebApiHandler_getUserCompanyProperties({ zuid: userToken })
        .then((result) => {
          const properties = result.properties
            .filter((property) => !onlyUnpaidProperties || property.isFreeBonusListing)
            .map((property) => CompanyProperty(property));

          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_SUCCESS,
            payload: { properties },
          });
        })
        .catch((error) => {
          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_FAIL,
            payload: { error: error },
          });
        });
    };
  },
  getAvailableProperties(userBrand: string, userToken: string) {
    // @ts-ignore
    return (dispatch) =>
      UmcCompanyOverviewWebApiHandler_getUserCompanyProperties({ zuid: userToken }).then((result) => {
        const availableProperties = result.properties.map((property) => CompanyProperty(property));

        return dispatch({
          type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_AVAILABLE_PROPERTIES,
          payload: { availableProperties },
        });
      });
  },
  getPendingProfile(email: string) {
    // @ts-ignore
    return (dispatch) => {
      dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_LOADING });
      return RentalManagerUserManagementCompanyWebApiHandler_getPendingProfile({ email })
        .then((result) => {
          const user = CompanyUser(result.userProfiles[0]);

          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_SUCCESS,
            payload: { user },
          });
        })
        .catch((error) => {
          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_INFO_FAIL,
            payload: { error: error },
          });
        });
    };
  },
  getPendingUserProperties(email: string, onlyUnpaidProperties = false) {
    // @ts-ignore
    return (dispatch) => {
      dispatch({ type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_LOADING });
      return RentalManagerUserManagementCompanyWebApiHandler_getPendingUserProperties({ email })
        .then((result) => {
          const properties = result.properties
            .filter((property) => !onlyUnpaidProperties || property.isOlympusT3)
            .map((property) => CompanyProperty(property));

          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_SUCCESS,
            payload: { properties },
          });
        })
        .catch((error) => {
          return dispatch({
            type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_FAIL,
            payload: { error: error },
          });
        });
    };
  },
  // @ts-ignore
  editableUserSetPermission(permission, value) {
    return {
      type: DispatchConstants.USER_MANAGEMENT_SET_EDITABLE_USER_PERMISSION,
      payload: {
        permission,
        value,
      },
    };
  },
  currentUserSetPermissions(permissions: unknown) {
    return {
      type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_PERMISSIONS,
      payload: {
        permissions,
      },
    };
  },
  currentUserSetProperties(properties: unknown) {
    return {
      type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_PROPERTIES,
      payload: {
        properties,
      },
    };
  },
  currentUserSetName(userName: string) {
    return {
      type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_NAME,
      payload: {
        userName,
      },
    };
  },
  currentUserSetEmail(email: string) {
    return {
      type: DispatchConstants.USER_MANAGEMENT_SET_CURRENT_USER_EMAIL,
      payload: {
        email,
      },
    };
  },
  editableUserSetLoadingState(loadingState: string) {
    return {
      type: DispatchConstants.USER_MANAGEMENT_SET_EDITABLE_USER_LOADING_STATE,
      payload: {
        loadingState,
      },
    };
  },
  editableUserSave() {
    // @ts-ignore
    return async (dispatch, getState) => {
      dispatch(this.editableUserSetLoadingState(LoadingConstants.LOADING));
      const { currentUser } = getState().userManagement;
      const privileges = Object.keys(currentUser.permissions)
        .filter((roleName) => currentUser.permissions[roleName])
        .map(
          (roleName) =>
            UserPermissionsConstants.PERMISSION_ROLES[
              roleName as keyof typeof UserPermissionsConstants.PERMISSION_ROLES
            ],
        );
      const roleTypes = Object.keys(currentUser.permissions)
        .filter((permission) => currentUser.permissions[permission])
        .map(
          (permission) =>
            UserPermissionsConstants.ROLES_TYPE_BY_PERMISSION[
              permission as keyof typeof UserPermissionsConstants.ROLES_TYPE_BY_PERMISSION
            ],
        );

      const inviteParams: UserManagementInviteUserRequestDto = {
        email: currentUser.email,
        userName: currentUser.userName,
        privileges,
        roleTypes,
        properties: [],
        propertiesToAdd: [],
      };

      let updateUserParams: RentalManagerUserProfileUpdateRequestDto = {
        ...inviteParams,
        userBrand: '',
        userToken: '',
        propertiesToRemove: [],
      };

      const specificProperties =
        !roleTypes.find((roleType) => roleType === CometUserRoleType.PROPERTY_MANAGER_COMPANY_ADMIN) &&
        !roleTypes.find((roleType) => roleType === CometUserRoleType.PROPERTY_MANAGER_ALL_PROPERTIES);

      if (specificProperties) {
        const selectedProperties = currentUser.properties.map((property: CompanyPropertyType) => property.listingId);
        const originalProperties = currentUser.originalProperties.map(
          (property: CompanyPropertyType) => property.listingId,
        );
        inviteParams.propertiesToAdd = currentUser.isPending
          ? selectedProperties
          : selectedProperties.filter((id: string) => !originalProperties.includes(id));

        inviteParams.properties = selectedProperties;

        updateUserParams.propertiesToRemove = originalProperties.filter(
          (id: string) => !selectedProperties.includes(id),
        );
        updateUserParams = { ...updateUserParams, ...inviteParams };
      }
      try {
        if (currentUser.isPending || !currentUser.userToken) {
          // create or update invitation
          await UmcCompanyOverviewWebApiHandler_createUserInvitation(inviteParams);
        } else {
          updateUserParams.userBrand = currentUser.userBrand;
          updateUserParams.userToken = currentUser.userToken;
          await RentalManagerUserManagementCompanyWebApiHandler_updateUserSettings(updateUserParams);
          // refresh user
          await dispatch(this.getCurrentUserProperties(currentUser.userBrand, currentUser.userToken));
        }
        dispatch(this.editableUserSetLoadingState(LoadingConstants.SUCCESS));
      } catch (e) {
        dispatch(this.editableUserSetLoadingState(LoadingConstants.ERROR));
        throw new Error(e);
      }
    };
  },
  initInvitation() {
    return {
      type: DispatchConstants.USER_MANAGEMENT_INIT_INVITATION,
    };
  },
  currentUserInit() {
    return {
      type: DispatchConstants.USER_MANAGEMENT_CURRENT_USER_INIT,
    };
  },
};

export default UserManagementActions;
